// src/features/super-admins/components/SuperAdminTable.tsx

import { useMemo, useState, useEffect } from "react";
import { Grid } from "gridjs-react";
import { h } from "gridjs";
import "gridjs/dist/theme/mermaid.css";
import type { SuperAdminRecord, SuperAdminFilters } from "../types";

interface SuperAdminTableProps {
  data: SuperAdminRecord[];
  isLoading: boolean;
  filters: SuperAdminFilters;
  onFiltersChange: (filters: SuperAdminFilters) => void;
  onEdit: (record: SuperAdminRecord) => void;
  onToggleStatus: (record: SuperAdminRecord) => void;
  onDelete: (record: SuperAdminRecord) => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

export function SuperAdminTable({
  data,
  isLoading: _isLoading,
  filters,
  onFiltersChange,
  onEdit,
  onToggleStatus,
  onDelete,
  selectedIds,
  onSelectionChange,
}: SuperAdminTableProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? "");
  const [statusValue, setStatusValue] = useState<SuperAdminFilters["status"]>(filters.status ?? "all");

  useEffect(() => {
    setSearchInput(filters.search ?? "");
  }, [filters.search]);

  useEffect(() => {
    setStatusValue(filters.status ?? "all");
  }, [filters.status]);

  /** Tạo map để lookup record nhanh */
  const recordMap = useMemo(
    () => new Map(data.map((r) => [r.id, r])),
    [data]
  );

  /** TABLE DATA */
  const gridData = useMemo(
    () =>
      data.map((r, i) => ({
        checkbox: r.id,
        stt: i + 1,
        name: r.full_name || r.user_name || "–",
        email: r.email ?? "–",
        phone: r.phone ?? "–",
        status: r.status,
        actions: r.id,
      })),
    [data]
  );

  /** PURE FORMATTERS – bắt buộc với GridJS 6.1.1 */
  // Không dùng factory pattern, không dùng hook bên trong formatter
  const formatCheckbox = (cell: unknown) => {
    const recordId = cell as string;
    const record = recordMap.get(recordId);
    if (!record) return h("span", {}, "–");

    return h(
      "div",
      { style: { display: "flex", justifyContent: "center" } },
      h("input", {
        type: "checkbox",
        checked: selectedIds.includes(record.id),
        onChange: (e: Event) => {
          const input = e.target as HTMLInputElement;
          if (input.checked) {
            onSelectionChange([...new Set([...selectedIds, record.id])]);
          } else {
            onSelectionChange(selectedIds.filter((id) => id !== record.id));
          }
        },
      })
    );
  };

  const formatSTT = (cell: unknown) => {
    return h("span", {}, String(cell ?? "–"));
  };

  const formatText = (cell: unknown, maxWidth: number) => {
    const text = typeof cell === "string" ? cell : "–";
    return h(
      "span",
      {
        className: "truncate-cell",
        title: text,
        style: { maxWidth: `${maxWidth}px`, display: "inline-block" },
      },
      text
    );
  };

  const formatStatus = (cell: unknown) => {
    const isActive = cell === "active";
    return h(
      "span",
      {
        className: isActive ? "badge bg-success" : "badge bg-secondary",
        style: { minWidth: 72, display: "inline-block", textAlign: "center" },
      },
      isActive ? "Hoạt động" : "Vô hiệu hóa"
    );
  };

  const formatActions = (cell: unknown) => {
    const recordId = cell as string;
    const record = recordMap.get(recordId);
    if (!record) return h("span", {}, "–");

    return h("div", { className: "table-actions" }, [
      h(
        "button",
        {
          className: "btn btn-sm",
          title: "Chỉnh sửa",
          onClick: () => onEdit(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h("i", { className: "ti ti-edit" })
      ),
      h(
        "button",
        {
          className: "btn btn-sm",
          title: record.status === "active" ? "Vô hiệu hóa" : "Kích hoạt",
          onClick: () => onToggleStatus(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h("i", {
          className: record.status === "active" ? "ti ti-check" : "ti ti-x",
        })
      ),
      h(
        "button",
        {
          className: "btn btn-sm btn-danger",
          title: "Xóa",
          onClick: () => onDelete(record),
          style: { width: 32, height: 32, padding: 0 },
        },
        h("i", { className: "ti ti-trash" })
      ),
    ]);
  };

  /** Tạo checkbox header với indeterminate state */
  const checkboxHeaderElement = useMemo(() => {
    const allSelected = data.length > 0 && selectedIds.length === data.length;
    const checkboxId = "super-admin-select-all-checkbox";

    return h("div", { style: { display: "flex", justifyContent: "center" } }, [
      h("input", {
        id: checkboxId,
        type: "checkbox",
        checked: allSelected,
        onChange: (e: Event) => {
          const input = e.target as HTMLInputElement;
          if (input.checked) {
            onSelectionChange(data.map((x) => x.id));
          } else {
            onSelectionChange([]);
          }
        },
      }),
    ]);
  }, [data, selectedIds, onSelectionChange]);

  /** Set indeterminate state cho select-all checkbox sau khi render */
  useEffect(() => {
    const checkbox = document.getElementById(
      "super-admin-select-all-checkbox"
    ) as HTMLInputElement;
    if (checkbox) {
      const someSelected =
        selectedIds.length > 0 && selectedIds.length < data.length;
      checkbox.indeterminate = someSelected;
    }
  }, [selectedIds, data.length]);

  /** COLUMNS - không dùng useMemo để tránh dependency issues với formatters */
  const columns = [
    {
      id: "checkbox",
      name: checkboxHeaderElement,
      formatter: formatCheckbox,
      width: "60px",
      sort: false, // Disable sort cho checkbox
    },
    {
      id: "stt",
      name: "#",
      formatter: formatSTT,
      width: "60px",
      sort: false, // Disable sort cho STT
    },
    {
      id: "name",
      name: "Họ tên",
      formatter: (c: unknown) => formatText(c, 200),
      width: "200px",
    },
    {
      id: "email",
      name: "Email",
      formatter: (c: unknown) => formatText(c, 240),
      width: "240px",
    },
    {
      id: "phone",
      name: "SĐT",
      formatter: (c: unknown) => formatText(c, 140),
      width: "140px",
    },
    {
      id: "status",
      name: "Trạng thái",
      formatter: formatStatus,
      width: "140px",
      sort: false, // Disable sort cho trạng thái
    },
    {
      id: "actions",
      name: "Hành động",
      formatter: formatActions,
      width: "160px",
      sort: false, // Disable sort cho hành động
    },
  ];

  return (
    <div>
      {/* Toolbar */}
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div
          className="toolbar-actions"
          style={{ gap: "12px", alignItems: "center", flexWrap: "wrap", display: "flex" }}
        >
          <select
            className="status-filter__select"
            value={statusValue}
            onChange={(event) => {
              const nextStatus = event.target.value as SuperAdminFilters["status"];
              setStatusValue(nextStatus);
              onFiltersChange({ ...filters, status: nextStatus, search: searchInput });
            }}
            aria-label="Lọc trạng thái Super Admin"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="inactive">Vô hiệu hoá</option>
          </select>
          <input
            type="search"
            className="form-control form-control-sm toolbar-input"
            style={{ width: 240 }}
            placeholder="Tìm kiếm email, tên..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              onFiltersChange({ ...filters, search: e.target.value, status: statusValue });
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="vuexy-table-wrapper">
        <Grid
          data={gridData}
          columns={columns}
          search={false}
          sort={true}
          pagination={{ limit: 10, summary: true }}
          className={{
            table: "vuexy-table",
          }}
          language={{
            pagination: {
              previous: "‹",
              next: "›",
              showing: "Hiển thị",
              results: () => "kết quả",
            },
          }}
        />
      </div>
    </div>
  );
}
